import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ExternalLink, Plus, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSession } from '@/lib/platform/hooks';
import { leaveAfterSignOut } from '@/lib/platform/routing';
import { ui, useOverlayOpen } from '@/app/context';
import { useHive, useHiveActions } from './state';

export function HiveRegisterPage() {
  const session = useSession();
  const actions = useHiveActions();
  const isOpen = useOverlayOpen('registerHiveDialog');
  const snapshot = useHive((value) => value);
  const [apiaryName, setApiaryName] = useState('');
  const [hiveNumber, setHiveNumber] = useState('');
  const [queenYear, setQueenYear] = useState('');
  const [colonyStrain, setColonyStrain] = useState('');

  useEffect(() => {
    actions.loadHives();
    // The feature actor owns the read lifecycle; mount is the one load trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openRegister = () => actions.openRegister();
  const submit = () => {
    const year = Number(queenYear);
    if (!apiaryName.trim() || !hiveNumber.trim() || !colonyStrain.trim() || !Number.isFinite(year)) return;
    actions.register({
      apiaryName: apiaryName.trim(),
      hiveNumber: hiveNumber.trim(),
      queenYear: year,
      colonyStrain: colonyStrain.trim(),
    });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-background/95">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-5">
          <Link
            to="/hives"
            className="font-heading text-base font-semibold tracking-tight"
            data-jr-key="hives-nav-link"
          >
            Hive Register
          </Link>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span className="hidden sm:inline">{session.user?.name ?? session.user?.email}</span>
            <Button variant="ghost" size="sm" data-jr-key="sign-out" onClick={() => void leaveAfterSignOut()}>
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 sm:py-16">
        <div className="flex flex-col justify-between gap-6 border-b border-border pb-8 sm:flex-row sm:items-end">
          <div className="max-w-xl">
            <h1 className="flex items-center gap-2 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
              <ShieldCheck className="size-6 text-primary" /> Your hive register
            </h1>
            <p className="mt-3 text-base leading-7 text-muted-foreground">
              Keep each colony’s details together and return to its official receipt whenever you need it.
            </p>
          </div>
          <Button
            className="w-full sm:w-auto"
            onClick={openRegister}
            disabled={!snapshot.can({ type: 'hive.open-register' })}
            data-jr-key="register-hive-open"
          >
            <Plus /> Register a hive
          </Button>
        </div>

        {snapshot.matches('loading') || snapshot.matches('idle') ? (
          <div className="mt-8 space-y-3" aria-label="Loading hives">
            <div className="h-14 animate-pulse rounded-lg bg-muted" />
            <div className="h-14 animate-pulse rounded-lg bg-muted" />
          </div>
        ) : snapshot.matches('error') ? (
          <section className="mt-8 rounded-xl border border-destructive/30 bg-destructive/5 p-6">
            <h2 className="font-heading text-lg font-semibold">Register unavailable</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              We could not load your hives right now. Try again when your connection is stable.
            </p>
            <Button className="mt-4" variant="outline" onClick={actions.loadHives} data-jr-key="hives-retry">
              Try again
            </Button>
          </section>
        ) : snapshot.context.hives.length === 0 ? (
          <section className="mt-8 rounded-xl border border-dashed border-border p-10 text-center">
            <h2 className="font-heading text-xl font-semibold">No hives registered yet</h2>
            <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted-foreground">
              Start your register with the first colony. Your registry receipt will stay here for easy reference.
            </p>
            <Button
              className="mt-5"
              onClick={openRegister}
              disabled={!snapshot.can({ type: 'hive.open-register' })}
              data-jr-key="register-hive-open-empty"
            >
              <Plus /> Register your first hive
            </Button>
          </section>
        ) : (
          <div className="mt-8 overflow-hidden rounded-xl border border-border">
            <div className="grid grid-cols-[1fr_auto] gap-4 border-b border-border bg-muted/40 px-5 py-3 text-xs font-medium uppercase tracking-wide text-muted-foreground sm:grid-cols-[1.1fr_1fr_1fr_1fr_auto]">
              <span>Hive</span>
              <span className="hidden sm:block">Apiary</span>
              <span className="hidden sm:block">Queen year</span>
              <span className="hidden sm:block">Strain</span>
              <span>Receipt</span>
            </div>
            {snapshot.context.hives.map((hive) => (
              <div
                key={hive.hiveId}
                className="grid grid-cols-[1fr_auto] items-center gap-4 border-b border-border px-5 py-4 last:border-0 sm:grid-cols-[1.1fr_1fr_1fr_1fr_auto]"
              >
                <div>
                  <p className="font-medium">{hive.hiveNumber || 'Unnamed hive'}</p>
                  <p className="text-sm text-muted-foreground sm:hidden">{hive.apiaryName || 'Apiary not named'}</p>
                </div>
                <span className="hidden text-sm sm:block">{hive.apiaryName || '—'}</span>
                <span className="hidden text-sm tabular-nums sm:block">{hive.queenYear ?? '—'}</span>
                <span className="hidden text-sm sm:block">{hive.colonyStrain || '—'}</span>
                {hive.url ? (
                  <Button variant="link" size="sm" render={<a href={hive.url} target="_blank" rel="noreferrer" />}>
                    <ExternalLink /> Receipt
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground">Receipt pending</span>
                )}
              </div>
            ))}
          </div>
        )}
        {snapshot.matches('ready') && snapshot.context.error ? (
          <p className="mt-4 text-sm text-destructive" role="alert">
            Your hive was not registered. Please review the details and try again.
          </p>
        ) : null}
      </main>

      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) ui.closeOverlay('registerHiveDialog');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Register a hive</DialogTitle>
            <DialogDescription>Add the details as they appear in your apiary records.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="apiary-name">Apiary name</Label>
              <Input
                id="apiary-name"
                value={apiaryName}
                onChange={(event) => setApiaryName(event.target.value)}
                data-jr-key="register-hive-apiary-name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="hive-number">Hive number</Label>
              <Input
                id="hive-number"
                value={hiveNumber}
                onChange={(event) => setHiveNumber(event.target.value)}
                data-jr-key="register-hive-hive-number"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="queen-year">Queen year</Label>
              <Input
                id="queen-year"
                inputMode="numeric"
                value={queenYear}
                onChange={(event) => setQueenYear(event.target.value)}
                data-jr-key="register-hive-queen-year"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="colony-strain">Colony strain</Label>
              <Input
                id="colony-strain"
                value={colonyStrain}
                onChange={(event) => setColonyStrain(event.target.value)}
                data-jr-key="register-hive-colony-strain"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => ui.closeOverlay('registerHiveDialog')}
              data-jr-key="register-hive-cancel"
            >
              Cancel
            </Button>
            <Button onClick={submit} disabled={snapshot.matches('registering')} data-jr-key="register-hive-submit">
              {snapshot.matches('registering') ? 'Registering…' : 'Register hive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
